import type { FC, KeyboardEvent, ReactNode } from 'react'
import type { Theme } from '@/components/generals/theme-provider'

import { useRef } from 'react'
import { Check, Monitor, Moon, Sun } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Switch } from '@/components/ui/switch'
import { Label } from '@/components/ui/label'
import { SectionCard } from '@/components/dashboard/section-card'
import { useTheme } from '@/components/generals/theme-provider'
import { useDashboard } from '@/components/dashboard/dashboard-provider'

const THEMES: { value: Theme, label: string, description: string, icon: typeof Sun }[] = [
    { value: 'light', label: 'Terang', description: 'Latar terang untuk siang hari', icon: Sun },
    { value: 'dark', label: 'Gelap', description: 'Nyaman di ruangan redup', icon: Moon },
    { value: 'system', label: 'Ikuti sistem', description: 'Menyesuaikan perangkat Anda', icon: Monitor }
]

interface PreviewProps {
    variant: 'light' | 'dark' | 'system'
}

// Small static mockup of the dashboard in each theme
const ThemePreview: FC<PreviewProps> = ({ variant }): ReactNode => {
    const half = (dark: boolean): ReactNode => (
        <div className={cn('flex h-full', dark ? 'bg-[#141210]' : 'bg-[#f9f8f6]')}>
            <div className={cn('w-1/4', dark ? 'bg-[#0f0d0b]' : 'bg-[#1c1916]')} />
            <div className='flex flex-1 flex-col gap-1.5 p-2'>
                <div className={cn('h-1.5 w-1/2 rounded-full', dark ? 'bg-white/20' : 'bg-black/15')} />
                <div className={cn('flex-1 rounded-md', dark ? 'bg-[#1c1a17]' : 'bg-white shadow-sm')}>
                    <div className='m-1.5 h-1 w-1/3 rounded-full bg-[#e66a2c]' />
                </div>
            </div>
        </div>
    )

    if (variant === 'system') {
        return (
            <div className='relative h-20 overflow-hidden rounded-md border'>
                <div className='absolute inset-0'>{half(false)}</div>
                <div className='absolute inset-0 [clip-path:polygon(100%_0,100%_100%,0_100%)]'>{half(true)}</div>
            </div>
        )
    }

    return <div className='h-20 overflow-hidden rounded-md border'>{half(variant === 'dark')}</div>
}

export const AppearanceForm: FC = (): ReactNode => {
    const { theme, setTheme } = useTheme()
    const { collapsed, setCollapsed } = useDashboard()
    const refs = useRef<(HTMLButtonElement | null)[]>([])

    const onKeyDown = (event: KeyboardEvent<HTMLButtonElement>, index: number): void => {
        const step = event.key === 'ArrowRight' || event.key === 'ArrowDown' ? 1 : event.key === 'ArrowLeft' || event.key === 'ArrowUp' ? -1 : 0
        if (step === 0) return

        event.preventDefault()
        const next = (index + step + THEMES.length) % THEMES.length
        setTheme(THEMES[next].value)
        refs.current[next]?.focus()
    }

    return (
        <SectionCard
            title='Tampilan'
            description='Perubahan langsung diterapkan dan tersimpan di perangkat ini.'
        >
            <div role='radiogroup' aria-label='Tema tampilan' className='grid gap-3 sm:grid-cols-3'>
                {THEMES.map((option, index) => {
                    const selected = theme === option.value
                    const Icon = option.icon

                    return (
                        <button
                            key={option.value}
                            ref={(element) => {
                                refs.current[index] = element
                            }}
                            type='button'
                            role='radio'
                            aria-checked={selected}
                            tabIndex={selected ? 0 : -1}
                            onClick={() => setTheme(option.value)}
                            onKeyDown={(event) => onKeyDown(event, index)}
                            className={cn(
                                'relative rounded-xl border p-3 text-left transition-colors outline-none focus-visible:ring-3 focus-visible:ring-ring/50',
                                selected ? 'border-foreground/60 ring-1 ring-foreground/60' : 'hover:border-foreground/25'
                            )}
                        >
                            <ThemePreview variant={option.value} />

                            <span className='mt-3 flex items-center gap-2 text-sm font-medium text-foreground'>
                                <Icon className='size-4 text-muted-foreground' aria-hidden='true' />
                                {option.label}
                            </span>
                            <span className='mt-0.5 block text-xs text-muted-foreground'>
                                {option.description}
                            </span>

                            {selected && (
                                <span className='absolute top-5 right-5 flex size-5 items-center justify-center rounded-full bg-foreground text-background' aria-hidden='true'>
                                    <Check className='size-3' />
                                </span>
                            )}
                        </button>
                    )
                })}
            </div>

            <div className='mt-6 flex items-start justify-between gap-6 border-t pt-5'>
                <div className='min-w-0'>
                    <Label htmlFor='compact-sidebar' className='text-sm font-medium'>
                        Sidebar ringkas
                    </Label>
                    <p id='compact-sidebar-description' className='mt-1 text-xs leading-relaxed text-muted-foreground'>
                        Hanya tampilkan ikon menu di layar lebar agar area kerja lebih luas. Bisa juga dengan Ctrl B.
                    </p>
                </div>

                <Switch
                    id='compact-sidebar'
                    checked={collapsed}
                    onCheckedChange={(checked) => setCollapsed(checked)}
                    aria-describedby='compact-sidebar-description'
                    className='mt-0.5'
                />
            </div>
        </SectionCard>
    )
}
